import { action } from '@play-co/replicant';
import createActions, { SyncActionAPI } from './createActions';
import * as Fate from '../../fate';
import * as Level from '../../level';
import * as Social from 'src/replicant/social';
import { duration } from 'src/replicant/utils/duration';
import * as Pet from 'src/replicant/pet';
import { State } from 'src/replicant/State';
import * as Match2 from 'src/replicant/match2';
import { acceptEvolution } from 'src/replicant/pet/pet.modifiers';
import { clearOldRaces } from 'src/replicant/match2/races/races.modifiers';
import * as match2FrenzyModifiers from 'src/replicant/match2/frenzy/match2-frenzy.modifiers';
import { cleanupOldLeagues } from 'src/replicant/match2/leagues/leagues.modifiers';
import { clearClaimedOpponentLists } from 'src/replicant/match2/tournament/match2-tournament.modifiers';
import * as History from 'src/replicant/history';
import * as Inventory from 'src/replicant/inventory';
import * as Friends from 'src/replicant/friends';
import { updateStreakBattleRoyaleState } from 'src/replicant/streakbattleroyale/streakbattleroyale.modifiers';
import { setCurrentRegion } from 'src/replicant/match2/regions/regions.modifiers';
import { getRegionId } from 'src/replicant/match2/regions/regions.getters';
import { resetRubberbandingLadderStep } from 'src/replicant/match2/rubberbanding/match2-rubberbanding.modifiers';
import { isReactivated } from 'src/replicant/resurrection/resurrection.getters';
import { buddiesCleanupOldEvents } from 'src/replicant/buddies/buddies.modifiers';
import { updateRaceBotsOnLogin } from 'src/replicant/match2/races/races.bots';
import {
  AssignManualAbTestArgs,
  assignManualAbTests,
} from 'src/replicant/ab/ab.modifiers';
import { puzzleCoinsIsPlayingTutorialDeterministic } from 'src/replicant/puzzlecoins/puzzlecoins.getters';
import { puzzleCoinsSetPetInitialState } from 'src/replicant/puzzlecoins/puzzlecoins.modifiers';
import {
  cleanupOldResolvedPendingPayments,
  recordMissedPayments,
} from 'src/replicant/iap/iap.pending.payments';
import { isPetStarterPackEnabled } from 'src/replicant/starterpack/starterpack.getters';
import { initPetStarterPackPromo } from 'src/replicant/starterpack/starterpack.modifiers';
import { isDynamicTestInBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import { sendAnalyticsEvents } from 'src/replicant/analytics';
import { setEntryTimestampWithArgs } from 'src/replicant/entry/entry.modifiers';
import {
  scheduleWeeklyLeaderboardMessages,
  cleanupOldWeeklyLeaderboardEntries,
} from 'src/replicant/weeklyLeaderboard/weeklyLeaderboard.modifiers';
import { restoreGuestStateFromLocalStorage } from 'src/replicant/guest/guest.modifiers';
import { cleanupOldTreasureExpressEntries } from 'src/replicant/treasureExpress/treasureExpress.modifiers';
import { scheduleGuestNotifications } from 'src/replicant/guest/guest.chatbot';
import { cleanupVisitSession } from 'src/replicant/visit/visit.modifiers';
import { assignRichMenu } from 'src/replicant/chatbot/chatbot.modifiers';
import { getDefaultRichMenu } from 'src/replicant/chatbot/chatbot.getters';
import { jankenCleanupExpired } from 'src/replicant/janken/janken.modifiers';
import { cardsCleanupExpiredTradesWithAnalytics } from 'src/replicant/cards/cards.modifiers';
import { dreamDriftResolveInstance } from 'src/replicant/dreamDrift/dreamDrift.modifiers';
import { isTutorialStepComplete } from 'src/replicant/tutorial/tutorial.getters';
import { setTutorialStepComplete } from 'src/replicant/tutorial/tutorial.modifiers';
import { cancelPreEntryMessages } from '../../chatbot/preEntry.modifiers';

export default createActions({
  /** Replicant onLogin action, ran on every login. */
  onLogin: action(
    (
      state,
      args: (AssignManualAbTestArgs & { offlineState?: string }) | undefined,
      api,
    ) => {
      // Only the first Enabled entry needs these deletions, before tutorial or pet setup.
      if (
        state.loginCount === 1 &&
        api.abTests.getBucketID(ab.tests.TEST_PRE_ENTRY_CHATBOT) === 'enabled'
      ) {
        cancelPreEntryMessages(api);
      }

      // Restore guest state from local storage on first login if applicable.
      if (args?.offlineState) {
        restoreGuestStateFromLocalStorage(state, args.offlineState, api);
      }

      // Reconcile completed food and toy purchases before tutorial-dependent login setup.
      if (
        state.care.careActionCount >= 2 &&
        isTutorialStepComplete(state, 'puzzleCoinsBasic') &&
        !isTutorialStepComplete(state, 'puzzleCoinsCare')
      ) {
        setTutorialStepComplete(state, 'puzzleCoinsCare');
      }

      // Assign manual AB tests on login, if args are provided.
      if (args) {
        assignManualAbTests(state, args, api);
      }

      // Just after A/B test assign we should update user rich menu
      assignRichMenu(state, getDefaultRichMenu(state), api);

      // If there is an active visit session, clear it.
      cleanupVisitSession(state, api);

      // Set initial inventory.
      Inventory.modifiers.setInitialInventory(state);

      // Unschedule chatbot messages.
      api.scheduledActions.unschedule('energyRefilled');

      delete state.deleteAccountIntent;

      const now = api.date.now();

      // Resolve any unfinished Dream Drift instance from the previous session.
      dreamDriftResolveInstance(state, now, api);

      // This can happen if the session ended before accepting the evolution.
      if (state.evolutionRewind) {
        acceptEvolution(state, now);
      }

      // Daycare must be taken into consideration
      const daycareEnd = Fate.getters.getPermanentStatusEffectEndTime(
        state,
        'daycare',
        now,
      );

      const syncTime = Math.max(now, daycareEnd ?? 0);

      // Sync the pet attributes to apply debuffs before removing them
      Pet.modifiers.syncAttributes(state, syncTime);

      // Clear asleep status effect in case it was mistakenly persisted (LPG-1730)
      Fate.modifiers.resolveStatusEffect(state, 'asleep', false, now);

      // Ensure pet to be in a state that the user can take care of it
      if (puzzleCoinsIsPlayingTutorialDeterministic(state)) {
        puzzleCoinsSetPetInitialState(state, now);
      }

      // Update the pet's fate
      Fate.modifiers.update(state, now);

      // Remove the debuffs from friend visit
      Pet.modifiers.removeFriendVisitDebuffs(state);

      // Level up if user has enough XP. This can happen if the XP table is updated.
      sendAnalyticsEvents(api, Level.modifiers.levelUp(state));

      // Cleanup old history records
      History.modifiers.historyCleanup(state);

      // Claimed tournament events keep nothing but their result
      clearClaimedOpponentLists(state);

      // Activate invite challenge if available
      if (
        Social.getters.isInviteChallengeAvailable(state) &&
        state.social.challenge.startTime === 0
      ) {
        state.social.challenge.startTime = api.date.now();
      }

      scheduleReengagementMessages(state, api);

      // Purge old races.
      clearOldRaces(state, now);

      // Update race bots. Must be done before updating the last session time.
      updateRaceBotsOnLogin(state, now);

      state.previousSessionAt = state.lastSessionAt;
      state.lastSessionAt = now;
      if (isReactivated(state, now)) {
        state.lastReactivationAt = now;
      }

      // Update entry timestamps based on given args
      if (args) {
        setEntryTimestampWithArgs(state, args, now);
      }

      // Setup initial match2 state if necessary

      Match2.modifiers.resetMatch2PlayingState(state, now);
      Match2.modifiers.resetDailyBonusDailyStats(state, now);
      match2FrenzyModifiers.resetFrenzy(state, now);
      if (Match2.dailyChallengeGetters.isDailyChallengeAvailable(state)) {
        Match2.dailyChallengeModifiers.resetDailyChallenge(state, now);
      }

      // Update streak event state accordingly
      updateStreakBattleRoyaleState(state, now);

      // Clean old leagues.
      cleanupOldLeagues(state, now);

      // Clear old weekly leaderboard entries
      cleanupOldWeeklyLeaderboardEntries(state, now);

      // Clear old treasure express entries
      cleanupOldTreasureExpressEntries(state, now);

      // Cleanup past buddies events
      buddiesCleanupOldEvents(state, now);

      // Update the current region
      setCurrentRegion(state, getRegionId(state));

      // Cleanup old pending payments
      cleanupOldResolvedPendingPayments(state, now);

      // Reset janken daily stats and clean expired entries
      jankenCleanupExpired(state, now);

      // Cleanup expired card trades and emit their proposer-side expiry events.
      cardsCleanupExpiredTradesWithAnalytics(state, now, api);

      // Reconcile purchase history with pending payments.
      if (
        isDynamicTestInBucket(
          state,
          ab.tests.FEATURE_PURCHASE_HISTORY_RECONCILIATION,
          'enabled',
        )
      ) {
        recordMissedPayments(state, api.purchases.getPurchaseHistory(), now);
      }

      // Reset rubberbanding ladder step if user is resurrected.
      if (isReactivated(state, now)) {
        resetRubberbandingLadderStep(state, now);
      }

      // Cleanup old friend requests
      Friends.modifiers.cleanupFriendshipRequests(state, now);

      // Track newly received friend requests (HUD path)
      sendAnalyticsEvents(
        api,
        Friends.modifiers.trackReceivedFriendshipRequests(state, now),
      );

      // Emit sender-side FriendAdd once per request accepted while offline
      sendAnalyticsEvents(
        api,
        Friends.modifiers.trackAcceptedFriendshipRequests(state, now),
      );

      // Drop accepted requests once their FriendAdd is out
      Friends.modifiers.cleanupAcceptedFriendshipRequests(state);

      // Clear gift batch count and scheduled message.
      state.gifts.giftBatchCount = 0;
      api.scheduledActions.unschedule('batchedGiftOA');

      // Unschedule any status messages while users are online
      api.scheduledActions.unschedule('sendStatusChangedChatbotMessage');

      // Init pet starter pack promo if enabled
      if (isPetStarterPackEnabled(state)) {
        initPetStarterPackPromo(state, now);
      }

      // Schedule weekly leaderboard messages
      scheduleWeeklyLeaderboardMessages(state, api);

      // Schedule all guest notifications, if suitable
      scheduleGuestNotifications(state, api);
    },
  ),
});

function scheduleReengagementMessages(state: State, api: SyncActionAPI) {
  const now = api.date.now();

  // First unschedule any extisting
  api.scheduledActions.unschedule('reengagementD7');
  api.scheduledActions.unschedule('reengagementD14');
  api.scheduledActions.unschedule('reengagementD28');

  // Figure out delays.
  const { getSafeDelay } = Fate.getters;
  const d7Delay = getSafeDelay(state, now, duration({ days: 6, hours: 23 }));
  const d14Delay = getSafeDelay(state, now, duration({ days: 13, hours: 23 }));
  const d28Delay = getSafeDelay(state, now, duration({ days: 27, hours: 23 }));

  // Then schedule all
  api.scheduledActions.schedule.reengagement({
    args: { day: '7' },
    notificationId: 'reengagementD7',
    delayInMS: d7Delay,
  });

  api.scheduledActions.schedule.reengagement({
    args: { day: '14' },
    notificationId: 'reengagementD14',
    delayInMS: d14Delay,
  });

  api.scheduledActions.schedule.reengagement({
    args: { day: '28' },
    notificationId: 'reengagementD28',
    delayInMS: d28Delay,
  });
}
